/**
 * ReforecastPreviewModal — HITL preview for the "Smart Re-forecast" engine.
 *
 * Flow (bulk mode):
 *    Triage  →  Preview  →  Confirm & Apply
 *
 * Rules the backend enforces (shown here for transparency):
 *   • plannedDistribution rebuilt strictly inside [planStart, planFinish]
 *   • actualProgress pruned to [actualStart, actualFinish]
 *   • Past periods with actual qty       →  forecast = actual
 *   • Balance = scope − Σactual          →  distributed today → forecastFinish
 *   • Actual ≥ scope                     →  no future forecast
 */
import { useEffect, useState, useMemo, Fragment } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Loader2, X, TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle,
  ShieldCheck, ClipboardList, Clock, User,
} from 'lucide-react';

function fmt(n, dp = 2) {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function DeltaChip({ v }) {
  if (Math.abs(v) < 0.01) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded"
            style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const pos = v > 0;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded"
          style={{ background: pos ? '#10b98118' : '#ef444418', color: pos ? '#10b981' : '#ef4444' }}>
      {pos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {pos ? '+' : ''}{fmt(v)}
    </span>
  );
}

function AnomalyChip({ count }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded"
          style={{ background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40' }}
          title="Data anomalies flagged by triage — the apply step will fix them.">
      <AlertTriangle className="w-2.5 h-2.5" /> {count}
    </span>
  );
}

function StatCard({ label, value, color, dp = 2, delta, testId }) {
  return (
    <div className="rounded-lg p-2.5 border" data-testid={testId}
         style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)' }}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-lg font-mono font-semibold mt-0.5" style={{ color }}>
        {typeof value === 'number' && dp === 0 ? value.toLocaleString() : value}
      </div>
      {typeof delta === 'number' && Math.abs(delta) > 0.01 && (
        <div className="mt-1"><DeltaChip v={delta} /></div>
      )}
    </div>
  );
}

const ISSUE_LABELS = {
  PLAN_OUT_OF_WINDOW:     'Plan rows outside plan window',
  ACTUAL_OUT_OF_WINDOW:   'Actual rows outside actual window',
  FORECAST_OUT_OF_WINDOW: 'Forecast rows outside forecast window',
  PLAN_SCOPE_MISMATCH:    'Σ Plan ≠ Scope',
  FORECAST_SCOPE_MISMATCH:'Σ Forecast ≠ Scope',
  ACTUAL_OVER_SCOPE:      'Σ Actual > Scope',
  MISSING_PLAN_DATES:     'Missing plan start/finish',
};

export default function ReforecastPreviewModal({ projectId, mode = 'bulk', activity = null, onClose, onApplied }) {
  const isSingle = mode === 'single';
  // Steps: bulk starts on "triage", single skips straight to "preview"
  const [step, setStep] = useState(isSingle ? 'preview' : 'triage');
  const [triage, setTriage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [error, setError] = useState(null);

  const previewUrl = isSingle
    ? `/v1/projects/${projectId}/fc/activities/${activity?.id}/reforecast/preview`
    : `/v1/projects/${projectId}/fc/reforecast/preview`;
  const applyUrl = isSingle
    ? `/v1/projects/${projectId}/fc/activities/${activity?.id}/reforecast/apply`
    : `/v1/projects/${projectId}/fc/reforecast/apply`;
  const triageUrl = `/v1/projects/${projectId}/fc/reforecast/triage`;
  const auditUrl  = `/v1/projects/${projectId}/fc/reforecast/audit?limit=1`;

  // ─── Load initial payload for the current step ──────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);

    const load = async () => {
      try {
        if (!isSingle && step === 'triage' && !triage) {
          const r = await api.post(triageUrl, {});
          if (cancelled) return;
          setTriage(r.data);
        } else if (step === 'preview' && !preview) {
          const r = await api.post(previewUrl, {});
          if (cancelled) return;
          setPreview(r.data);
        }
        // Always refresh the latest audit chip
        if (!audit) {
          try {
            const a = await api.get(auditUrl);
            if (!cancelled) setAudit((a.data?.runs || [])[0] || null);
          } catch { /* audit is best-effort */ }
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ─── Apply ────────────────────────────────────────────────────────
  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(applyUrl, {});
      const msg = isSingle
        ? `Re-forecast applied · balance ${fmt(data.balance)}`
        : `${data.applied} activity re-forecast${data.applied !== 1 ? 's' : ''} saved${data.failed ? ` · ${data.failed} failed` : ''}`;
      toast.success(msg);
      if (onApplied) onApplied(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  const singleDiff = isSingle && preview?.diff;
  const bulkActivities = !isSingle ? (preview?.activities || []) : [];
  const changedActivities = useMemo(
    () => bulkActivities.filter(a => Math.abs(a.delta) > 0.01),
    [bulkActivities]
  );
  const anomalyActivities = useMemo(
    () => (triage?.activities || []).filter(a => a.hasIssues),
    [triage]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.6)' }}
         data-testid="reforecast-preview-modal">
      <div className="w-full max-w-6xl max-h-[92vh] flex flex-col rounded-xl shadow-2xl"
           style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: '#f59e0b' }} />
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                Smart Re-forecast · {isSingle ? 'Activity Preview' : 'Project-wide'}
              </h3>
              {!isSingle && (
                <div className="flex items-center gap-1 ml-3 text-[11px]">
                  <StepPill active={step==='triage'}  onClick={() => setStep('triage')}  label="1 · Triage"  testId="rf-step-triage" />
                  <StepPill active={step==='preview'} onClick={() => setStep('preview')} label="2 · Preview" testId="rf-step-preview" />
                </div>
              )}
            </div>
            <p className="text-xs mt-1 max-w-3xl" style={{ color: 'var(--text-muted)' }}>
              Apply rebuilds each activity's daily plan / forecast / actual strictly inside its own date windows.
              Past actuals lock the past forecast. Balance <b>(scope − Σ actual)</b> re-distributes today → forecast finish.
              Nothing is saved until you click <b>Confirm & Apply</b>.
            </p>
            {audit && (
              <div className="mt-2 inline-flex items-center gap-3 text-[10px] px-2 py-1 rounded"
                   style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}
                   data-testid="reforecast-audit-chip">
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Last run: <b className="font-mono">{new Date(audit.runAt).toLocaleString()}</b></span>
                <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {audit.runBy?.email || audit.runBy?.name || 'system'}</span>
                <span>· {audit.mode} · {audit.activitiesApplied}/{audit.activitiesConsidered} applied · Δ scope <b className="font-mono" style={{ color: audit.scopeShift >= 0 ? '#10b981' : '#ef4444' }}>{fmt(audit.scopeShift)}</b></span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-sm p-1 rounded hover:opacity-70" data-testid="reforecast-close-btn">
            <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 py-10 justify-center text-sm" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              {step === 'triage' ? 'Running triage agent…' : 'Computing preview…'}
            </div>
          )}
          {!loading && error && (
            <div className="flex items-start gap-2 p-3 rounded text-xs"
                 style={{ background: '#ef444410', border: '1px solid #ef444440', color: '#ef4444' }}>
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div><div className="font-semibold">Failed</div><div>{error}</div></div>
            </div>
          )}

          {/* TRIAGE (bulk only) */}
          {!loading && !error && !isSingle && step === 'triage' && triage && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Activities Scanned" value={triage.totalActivities} color="#3b82f6" dp={0} testId="rf-triage-total" />
                <StatCard label="Activities w/ Issues" value={triage.activitiesWithIssues} color={triage.activitiesWithIssues > 0 ? '#f59e0b' : '#10b981'} dp={0} testId="rf-triage-issues" />
                <StatCard label="Plan Orphans" value={triage.totalPlanOrphans} color="#ef4444" dp={0} />
                <StatCard label="Actual Orphans" value={triage.totalActualOrphans} color="#ef4444" dp={0} />
                <StatCard label="Forecast Orphans" value={triage.totalForecastOrphans} color="#ef4444" dp={0} />
              </div>

              {Object.keys(triage.byIssueCode || {}).length > 0 && (
                <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b flex items-center gap-2"
                       style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                    <ClipboardList className="w-3 h-3" /> Anomaly categories
                  </div>
                  <div className="p-3 flex flex-wrap gap-2">
                    {Object.entries(triage.byIssueCode).map(([code, count]) => (
                      <span key={code}
                            className="text-[11px] px-2 py-1 rounded"
                            style={{ background: '#f59e0b18', color: '#f59e0b', border: '1px solid #f59e0b40' }}>
                        {ISSUE_LABELS[code] || code}: <b>{count}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                <div className="max-h-[46vh] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr style={{ background: 'var(--card-bg-alt)' }}>
                        <th className="p-2 text-left" style={{ color: 'var(--text-muted)' }}>Activity</th>
                        <th className="p-2 text-left" style={{ color: 'var(--text-muted)' }}>Disc</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Scope</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Σ Plan</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Σ Fcst</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Σ Actual</th>
                        <th className="p-2 text-center" style={{ color: 'var(--text-muted)' }}>Orphans (P/A/F)</th>
                        <th className="p-2 text-left" style={{ color: 'var(--text-muted)' }}>Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(triage.activities || []).slice(0, 300).map((a) => (
                        <tr key={a.id} className="border-t" style={{ borderColor: 'var(--border-color)',
                                                                     background: a.hasIssues ? '#f59e0b0a' : 'transparent' }}
                            data-testid={`rf-triage-row-${a.activityId}`}>
                          <td className="p-2">
                            <div className="font-mono font-medium text-[11px]" style={{ color: 'var(--accent-primary)' }}>{a.activityId}</div>
                            <div className="text-[10px] truncate max-w-[280px]" style={{ color: 'var(--text-muted)' }} title={a.activityName}>{a.activityName}</div>
                          </td>
                          <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.discipline}</td>
                          <td className="p-2 text-right font-mono" style={{ color: '#3b82f6' }}>{fmt(a.scope)}</td>
                          <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(a.planSum)}</td>
                          <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(a.forecastSum)}</td>
                          <td className="p-2 text-right font-mono" style={{ color: '#10b981' }}>{fmt(a.actualSum)}</td>
                          <td className="p-2 text-center font-mono text-[11px]"
                              style={{ color: (a.planOrphans+a.actualOrphans+a.forecastOrphans) > 0 ? '#ef4444' : 'var(--text-muted)' }}>
                            {a.planOrphans} / {a.actualOrphans} / {a.forecastOrphans}
                          </td>
                          <td className="p-2">
                            {a.issues.length === 0 ? (
                              <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: '#10b981' }}>
                                <ShieldCheck className="w-3 h-3" /> clean
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {a.issues.map((i, ix) => (
                                  <span key={ix} className="text-[9px] px-1.5 py-0.5 rounded"
                                        style={{ background: '#ef444418', color: '#ef4444' }}
                                        title={i.message}>
                                    {ISSUE_LABELS[i.code] || i.code}{typeof i.count === 'number' ? ` · ${i.count}` : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(triage.activities || []).length === 0 && (
                        <tr><td colSpan={8} className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                          No activities scanned.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Triage is read-only. Click <b>Next: Preview</b> to compute the diff, or <b>Cancel</b> to exit.
              </div>
            </>
          )}

          {/* SINGLE-ACTIVITY PREVIEW */}
          {!loading && !error && isSingle && preview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Scope" value={fmt(preview.scope)} color="#3b82f6" />
                <StatCard label="Actual to Date" value={fmt(preview.actualTotal)} color="#10b981" />
                <StatCard label="Balance" value={fmt(preview.balance)} color="#f59e0b" />
                <StatCard label="Old Forecast Σ" value={fmt(singleDiff?.oldTotal)} color="var(--text-muted)" />
                <StatCard label="New Forecast Σ" value={fmt(singleDiff?.newTotal)} color="#f59e0b" delta={singleDiff?.deltaTotal} />
              </div>

              {preview.triage?.hasIssues && (
                <div className="p-3 rounded flex items-start gap-2"
                     style={{ background: '#f59e0b0d', border: '1px solid #f59e0b40' }}>
                  <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: '#f59e0b' }} />
                  <div className="text-[11px]" style={{ color: 'var(--text-primary)' }}>
                    <b>{preview.triage.issues.length}</b> anomaly / anomalies detected. Applying will fix:
                    <ul className="mt-1 list-disc ml-4 space-y-0.5">
                      {preview.triage.issues.map((i, ix) => (
                        <li key={ix} style={{ color: 'var(--text-muted)' }}>{i.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <MonthDiffTable rows={singleDiff?.rows || []} title="Monthly Diff · Old Forecast → New Forecast" />
            </>
          )}

          {/* BULK PREVIEW */}
          {!loading && !error && !isSingle && step === 'preview' && preview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Activities Considered" value={preview.totalActivities} color="#3b82f6" dp={0} testId="rf-preview-total" />
                <StatCard label="Old Forecast Σ" value={fmt(preview.oldForecastGrandTotal)} color="var(--text-muted)" />
                <StatCard label="New Forecast Σ" value={fmt(preview.newForecastGrandTotal)} color="#f59e0b" />
                <StatCard label="Grand Δ" value={fmt(preview.grandDelta)} color={preview.grandDelta >= 0 ? '#10b981' : '#ef4444'} />
              </div>

              <div className="text-[11px] flex items-center gap-3" style={{ color: 'var(--text-muted)' }}>
                <span>
                  <b style={{ color: 'var(--text-primary)' }}>{changedActivities.length}</b> activities changed ·{' '}
                  <b style={{ color: 'var(--text-primary)' }}>{preview.totalActivities - changedActivities.length}</b> unchanged ·{' '}
                  <b style={{ color: '#f59e0b' }}>{preview.activitiesWithAnomalies || 0}</b> anomalies
                </span>
                <span className="ml-auto">Sorted by anomaly-count then absolute Δ · Click a row to expand monthly diff</span>
              </div>

              <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                <div className="max-h-[52vh] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr style={{ background: 'var(--card-bg-alt)' }}>
                        <th className="p-2 text-left" style={{ color: 'var(--text-muted)' }}>Activity</th>
                        <th className="p-2 text-left" style={{ color: 'var(--text-muted)' }}>Disc</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Scope</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Actual</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Balance</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Old Fcst</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>New Fcst</th>
                        <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Δ</th>
                        <th className="p-2 text-center" style={{ color: 'var(--text-muted)' }}>!</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkActivities.map((a) => {
                        const isOpen = !!expandedIds[a.id];
                        return (
                          <Fragment key={a.id}>
                            <tr className="border-t cursor-pointer hover:opacity-90"
                                style={{ borderColor: 'var(--border-color)', background: isOpen ? 'var(--card-bg-alt)' : 'transparent' }}
                                onClick={() => setExpandedIds((p) => ({ ...p, [a.id]: !p[a.id] }))}
                                data-testid={`reforecast-row-${a.activityId}`}>
                              <td className="p-2">
                                <div className="font-mono font-medium text-[11px]" style={{ color: 'var(--accent-primary)' }}>{a.activityId}</div>
                                <div className="text-[10px] truncate max-w-[280px]" style={{ color: 'var(--text-muted)' }} title={a.activityName}>{a.activityName}</div>
                              </td>
                              <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.discipline}</td>
                              <td className="p-2 text-right font-mono" style={{ color: '#3b82f6' }}>{fmt(a.scope)}</td>
                              <td className="p-2 text-right font-mono" style={{ color: '#10b981' }}>{fmt(a.actualTotal)}</td>
                              <td className="p-2 text-right font-mono" style={{ color: '#f59e0b' }}>{fmt(a.balance)}</td>
                              <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(a.oldForecastTotal)}</td>
                              <td className="p-2 text-right font-mono" style={{ color: '#f59e0b' }}>{fmt(a.newForecastTotal)}</td>
                              <td className="p-2 text-right"><DeltaChip v={a.delta} /></td>
                              <td className="p-2 text-center"><AnomalyChip count={a.anomalyCount} /></td>
                            </tr>
                            {isOpen && (
                              <tr style={{ background: 'var(--card-bg-alt)' }}>
                                <td colSpan={9} className="p-3">
                                  <MonthDiffTable rows={a.monthlyDiff || []} dense />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                      {bulkActivities.length === 0 && (
                        <tr><td colSpan={9} className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                          No activities to re-forecast.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t"
             style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
          <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {preview?.todayIso && <>Today = <b className="font-mono">{preview.todayIso}</b> · </>}
            No changes are persisted until you confirm.
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="reforecast-cancel-btn">Cancel</Button>
            {!isSingle && step === 'triage' && (
              <Button onClick={() => setStep('preview')} disabled={loading || !!error}
                      style={{ background: '#3b82f6', color: '#fff' }}
                      data-testid="reforecast-next-btn">
                Next: Preview →
              </Button>
            )}
            {(isSingle || step === 'preview') && (
              <Button onClick={apply} disabled={applying || loading || !!error}
                      style={{ background: '#f59e0b', color: '#fff' }}
                      data-testid="reforecast-apply-btn">
                {applying ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying…</>) : 'Confirm & Apply'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepPill({ active, onClick, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className="px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
            style={{
              background: active ? '#f59e0b' : 'transparent',
              color: active ? '#fff' : 'var(--text-muted)',
              border: '1px solid ' + (active ? '#f59e0b' : 'var(--border-color)'),
            }}>
      {label}
    </button>
  );
}

function MonthDiffTable({ rows, title, dense }) {
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
      {title && (
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b"
             style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
          {title}
        </div>
      )}
      <table className="w-full text-xs">
        <thead>
          <tr style={{ background: dense ? 'transparent' : 'var(--card-bg-alt)' }}>
            <th className="p-2 text-left"  style={{ color: 'var(--text-muted)' }}>Month</th>
            <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Old Fcst</th>
            <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>New Fcst</th>
            <th className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
              <td className="p-2 font-mono" style={{ color: 'var(--text-primary)' }}>{r.month}</td>
              <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(r.oldQty)}</td>
              <td className="p-2 text-right font-mono" style={{ color: '#f59e0b' }}>{fmt(r.newQty)}</td>
              <td className="p-2 text-right"><DeltaChip v={r.delta} /></td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
              No periods.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
